import { ChangeEvent, Dispatch as DispatchReact, SetStateAction, useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import type { Dispatch } from 'redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { SearchInput } from 'UIPackage/MainModule/components/SearchInput';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { IExport } from 'APIPackage/ImportExportModule/interfaces/IExport';
import { ISimilaritiesFilter } from 'APIPackage/SimilarityModule/interfaces/ISimilaritiesFilter';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeAction, removeNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { INotification } from 'CorePackage/NotificationModule/interfaces/INotification';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { compareDates, formatDateLocale, getFormatedClientTime } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_ISO_FNS, SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { adminGetFetchSettings } from 'PersonalAccountPackage/AdminModule/actions/adminAction';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { ExportFormatsEnum } from 'APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import { addNoticeImportNotification } from 'CorePackage/NotificationModule/common/addNoticeImportNotification';

import styles from './SimilaritiesPanel.module.scss';
import {
  similaritiesExportList,
  similaritiesGetData,
  similaritiesSetSearchString,
} from '../actions/similaritiesActions';
import {
  getSimilaritiesFilter,
  getSimilaritiesNotProcessedCount,
  getSimilaritiesTotalCount,
} from '../selectors/similaritiesSelectors';

const findDisabledSettings = (settings: IAdminSetting[], code: AdminSettingCodesEnum) => {
  return settings
    .filter(item => item.code === code)
    .reduce((isDisable, currentSettings) => {
      if (currentSettings.value) {
        const compare = compareDates({
          dateLeft: currentSettings.value,
          dateRight: getFormatedClientTime(SYSTEM_DATE_FORMAT_FNS),
        });

        return compare === 1;
      }
      if (currentSettings.periods && !isDisable) {
        return !!currentSettings.periods.find(item => {
          const compareStartDate = compareDates({
            dateLeft: formatDateLocale({
              date: item.startDate,
              dateFormat: DATE_FORMAT_ISO_FNS,
              parseFormat: DATE_FORMAT_ISO_FNS,
            }),
            dateRight: getFormatedClientTime(DATE_FORMAT_ISO_FNS),
          });
          const compareEndDate = compareDates({
            dateLeft: formatDateLocale({
              date: item.endDate,
              dateFormat: DATE_FORMAT_ISO_FNS,
              parseFormat: DATE_FORMAT_ISO_FNS,
            }),
            dateRight: getFormatedClientTime(DATE_FORMAT_ISO_FNS),
          });

          return (compareStartDate === 0 || compareStartDate === -1) && (compareEndDate === 0 || compareEndDate === 1);
        });
      }

      return isDisable;
    }, false);
};

interface ISimilaritiesPanelProps {
  setFiltersModalVisible: DispatchReact<SetStateAction<boolean>>;
  addNotice: (notice: INotification) => void;
  removeNotice: (id: string) => void;
}

interface ISimilaritiesPanelStoreProps {
  totalCount: number;
  filter: ISimilaritiesFilter;
  notProcessedCount: number;
  settings: IAdminSetting[];
}

const SimilaritiesPanelWithoutStore = ({
  setFiltersModalVisible,
  addNotice,
  removeNotice,
  totalCount,
  filter,
  notProcessedCount,
  settings,
}: ISimilaritiesPanelProps & ISimilaritiesPanelStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [inputError, setInputError] = useState('');
  const [isDisabledExport, setIsDisabledExport] = useState(false);
  const [isDisabledViewSimilarities, setIsDisabledViewSimilarities] = useState(false);

  useEffect(() => {
    dispatch(adminGetFetchSettings());
  }, [dispatch]);

  useEffect(() => {
    if (settings.length) {
      setIsDisabledExport(findDisabledSettings(settings, AdminSettingCodesEnum.KNM_MATCH_EXPORT_DATE));
      setIsDisabledViewSimilarities(findDisabledSettings(settings, AdminSettingCodesEnum.KNM_PLAN_MATCH_OUTPUT_DATE));
    }
  }, [settings]);

  useEffect(() => {
    const idExport = uuidv4();
    const idVisible = uuidv4();

    if (isDisabledExport) {
      addNotice({
        noticeType: NoticeTypesEnum.WARNING,
        renderContent: 'Экспорт списка совпадающих КНМ ограничен',
        messageType: MessageTypesEnum.EXPORT_DISABLED,
        id: idExport,
      });
    }
    if (isDisabledViewSimilarities) {
      addNotice({
        noticeType: NoticeTypesEnum.WARNING,
        renderContent: 'Отображение совпадающих КНМ ограничено',
        messageType: MessageTypesEnum.SIMILARITIES_DISABLED,
        id: idVisible,
      });
    }

    return () => {
      removeNotice(idExport);
      removeNotice(idVisible);
    };
  }, [addNotice, removeNotice, isDisabledExport, isDisabledViewSimilarities]);

  const exportNotify = ({ id }: IExport) => {
    dispatch(addNoticeImportNotification(id));
  };

  const handleExport = (format: ExportFormatsEnum) => async () =>
    dispatch(similaritiesExportList(format, filter, exportNotify));
  const actionItems = [
    {
      id: 'action-export-xlsx',
      content: <DropdownItem onClick={handleExport(ExportFormatsEnum.XLSX)}>Экспорт в XLSX</DropdownItem>,
    },
    {
      id: 'action-export-ods',
      content: <DropdownItem onClick={handleExport(ExportFormatsEnum.ODS)}>Экспорт в ODS</DropdownItem>,
    },
  ];

  const onOpenFilterModal = useCallback(() => setFiltersModalVisible(true), [setFiltersModalVisible]);

  const handleDropdownVisibleChange = useCallback((visible: boolean) => {
    setIsDropdownVisible(visible);
  }, []);

  const handleBlur = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { length } = e.target.value;

    if (length < 3 && length > 0) {
      setInputError('Длина строки должна быть не менее 3 символов');
    } else if (length > 1024) {
      setInputError('Превышена допустимая длина строки');
    } else {
      setInputError('');
    }
  }, []);

  const handleSearchInputSubmit = useCallback(() => {
    dispatch(similaritiesGetData());
  }, [dispatch]);

  const handleSearchInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(similaritiesSetSearchString(e.target.value));
    },
    [dispatch],
  );

  const buttonAttributes = {
    ...(isDropdownVisible && { 'data-active': true }),
    ...(isDisabledExport && { 'data-disable': true }),
  };

  return (
    <div className="shared-panel-container">
      <div className={styles.Actions}>
        <SearchInput
          className={styles.SearchInput}
          name="searchString"
          placeholder="Искать"
          error={inputError}
          onBlur={handleBlur}
          onSubmit={handleSearchInputSubmit}
          onChange={handleSearchInputChange}
        />
        <Tooltip
          placement="right"
          content={
            <div className={styles.TooltipContent}>
              Доступен поиск проверяемых лиц по следующим критериям:
              <ul>
                <li>Наименование проверяемого лица</li>
                <li>ИНН</li>
                <li>ОГРН</li>
              </ul>
            </div>
          }
        >
          <span className={styles.TooltipIcon} />
        </Tooltip>
      </div>
      <div className={styles.Filters}>
        <span>{`Не обработано: ${notProcessedCount} из ${totalCount} ЮЛ`}</span>
        <div className={styles.Wrapper}>
          <Button text="Фильтры" color="primary" onClick={onOpenFilterModal} />
          <Dropdown items={actionItems} onVisibleChange={handleDropdownVisibleChange} disabled={isDisabledExport}>
            {isDisabledExport ? (
              <Tooltip
                placement="top"
                content={
                  <div className={styles.TooltipContent}>Функционал временно ограничен администратором ФГИС ЕРП</div>
                }
              >
                <Button
                  className={styles.DropdownButton}
                  text="Действия"
                  id="visibleChangeActionsButton"
                  variant="contained"
                  {...buttonAttributes}
                />
              </Tooltip>
            ) : (
              <Button
                className={styles.DropdownButton}
                text="Действия"
                id="visibleChangeActionsButton"
                variant="contained"
                {...buttonAttributes}
              />
            )}
          </Dropdown>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): ISimilaritiesPanelStoreProps => ({
  totalCount: getSimilaritiesTotalCount(state),
  filter: getSimilaritiesFilter(state),
  notProcessedCount: getSimilaritiesNotProcessedCount(state),
  settings: getAdminFetchSettings(state),
});

const mapDispatchToProps = (dispatch: Dispatch<RootActions>) => ({
  addNotice: (notice: INotification) => dispatch(addNoticeAction(notice)),
  removeNotice: (id: string) => dispatch(removeNoticeAction(id)),
});

export const SimilaritiesPanel = connect(mapStateToProps, mapDispatchToProps)(SimilaritiesPanelWithoutStore);
